import classNames from 'classnames';
import { format as originalFormat, isAfter } from 'date-fns';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import { FlightTurnaroundDto, Resource, useQueryGetGetFlights } from '../../../../../../backend';
import { errorToastConfig, getTwoDaysAgo } from '../../../../../../utils/utils';
import {
    getTurnaroundActualOccupation,
    getTurnaroundScheduledOccupation,
    isTurnaroundScheduledOrActual,
} from '../../../RmsContent.utils';
import { useRmsGanttState, useRmsGanttValue, useSetRmsGantt } from '../../../RmsContext';
import { ganttDayFormat, ganttPxPerMs } from '../../gantt.constants';
import { getTurnaroundId } from '../../gantt.utils';
import { TurnaroundPopover } from '../TurnaroundForm/TurnaroundPopover';
import { DeviationBox } from './DeviationBox/DeviationBox';
import { StyledBox } from './GanttTurnaroundBox.style';
import { TurnaroundBox } from './TurnaroundBox/TurnaroundBox';

export const GanttTurnaroundBox = ({
    turnaround,
    resource,
}: {
    turnaround: FlightTurnaroundDto;
    resource?: Resource;
}) => {
    const dayBounds = useRmsGanttValue('dayBounds');
    const resourceType = useRmsGanttValue('resourceType');
    const day = useRmsGanttValue('day');
    const setSelectedTurnarounds = useSetRmsGantt('selectedTurnarounds');
    const setMousePositionY = useSetRmsGantt('mousePositionY');
    const setIsDragging = useSetRmsGantt('isDragging');
    const [isFocused, setIsFocused] = useRmsGanttState('isFocused');
    const nbUpdateAllocations = useRmsGanttValue('nbUpdateAllocations');
    const turnaroundBox = useRef(null);
    const rmsFilters = useRmsGanttValue('rmsFilters');
    const { data: ganttDto } = useQueryGetGetFlights({
        selectedDay: originalFormat(day, ganttDayFormat),
        selectedResourceType: resourceType,
    });

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const turnaroundOccupation = getTurnaroundActualOccupation(turnaround);

    const scheduledTurnaroundOccupation = getTurnaroundScheduledOccupation(turnaround);

    const isScheduledOrActual = isTurnaroundScheduledOrActual(
        day,
        turnaround.considerArrivalFlight
            ? turnaround.arrivalFlight?.actualResourceOccupations?.[resourceType]
            : turnaround.departureFlight?.actualResourceOccupations?.[resourceType],
        scheduledTurnaroundOccupation,
    );

    const handleClick = useCallback(
        e => {
            e.stopPropagation();
            e.preventDefault();

            const duplicateTurnarounds = ganttDto?.turnarounds?.filter(
                t => getTurnaroundId(t) === getTurnaroundId(turnaround),
            );
            if (duplicateTurnarounds?.length > 1) {
                setSelectedTurnarounds(duplicateTurnarounds);
            } else {
                setSelectedTurnarounds([turnaround]);
            }
            setIsFocused(true);
        },
        [ganttDto?.turnarounds, setIsFocused, setSelectedTurnarounds, turnaround],
    );

    const handleDoubleClick = useCallback(e => {
        e.stopPropagation();
        e.preventDefault();
        setAnchorEl(e.currentTarget);
    }, []);

    const dragStartHandler = useCallback(
        event => {
            if (event.target instanceof HTMLElement) {
                event.dataTransfer.setData(
                    'text/plain',
                    event.target.id +
                        ',' +
                        turnaround?.considerArrivalFlight +
                        ',' +
                        turnaround?.considerDepartureFlight,
                );
                event.dataTransfer.effectAllowed = 'copy';
                const image = document.createElement('canvas');
                event.dataTransfer.setDragImage(image, 0, 0);
                document.body?.appendChild(image);
                setSelectedTurnarounds([turnaround]);
                setIsDragging(true);
                setIsFocused(true);
                setMousePositionY(event.clientY);
            } else {
                event.preventDefault();
            }
        },
        [setSelectedTurnarounds, turnaround, setIsDragging, setIsFocused, setMousePositionY],
    );

    const dragHandler = useCallback(
        event => {
            event.preventDefault();
            setMousePositionY(event.clientY);
        },
        [setMousePositionY],
    );

    const dragEndHandler = useCallback(
        event => {
            event.preventDefault();
            if (event.dataTransfer.dropEffect === 'none') {
                setIsDragging(false);
                toast(<FormattedMessage id={'allocation.error'} />, errorToastConfig);
            }
        },
        [setIsDragging],
    );

    // Prevent drag&drop on turnaround that is multiDay with no arr or dep today
    const isDraggable =
        (turnaround.considerArrivalFlight || turnaround.considerDepartureFlight) && isAfter(day, getTwoDaysAgo());

    //to remove turnaround ghost when click away from the box
    //here: detect the click away
    useEffect(() => {
        if (!isFocused) {
            return;
        }

        const handleClickAway = e => {
            if (turnaroundBox.current && !turnaroundBox.current.contains(e.target)) {
                setIsFocused(false);
            }
        };
        // Bind the event listener
        document.addEventListener('click', handleClickAway);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('click', handleClickAway);
        };
    }, [isFocused, setIsFocused]);

    if (!turnaroundOccupation) {
        return null;
    }

    return (
        <>
            <StyledBox
                marginLeft={`${
                    ((turnaroundOccupation.occupationStart?.time - dayBounds.from) * 2 || 0) * ganttPxPerMs
                }px`}
                onClick={handleClick}
                onDoubleClick={nbUpdateAllocations === 0 ? handleDoubleClick : undefined}
                draggable={isDraggable}
                onDragStart={dragStartHandler}
                onDrag={dragHandler}
                onDragEnd={dragEndHandler}
                id={getTurnaroundId(turnaround)}
                ref={turnaroundBox}
                className={classNames({ isScheduledOrActual, isDraggable })}
            >
                <TurnaroundBox turnaround={turnaround} resource={resource} />
                {rmsFilters.viewModeScheduled === 'deviations' && (
                    <DeviationBox
                        turnaroundOccupation={turnaroundOccupation}
                        scheduledTurnaroundOccupation={scheduledTurnaroundOccupation}
                        minTimeBetweenUse={resource?.minTimeBetweenUse || 0}
                    />
                )}
            </StyledBox>
            <TurnaroundPopover anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
        </>
    );
};
